import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  makeStyles, mergeClasses, tokens,
  Button, Checkbox, Skeleton, SkeletonItem,
} from '@fluentui/react-components';
import { API_BASE, API_HOST } from '../config.js';
import { useAuth } from '../context/AuthContext';
import { iconMap } from '../theme/icons';
import { Card, Badge, Heading, Breadcrumb } from '../theme/primitives';
import { stonePalette } from '../theme/brand';
import { formatPrice } from '../utils/format';
import FurnitureDrawingTabs from './FurnitureDrawingTabs';
import AssemblyInstructionsModal from '../components/shop/AssemblyInstructionsModal';
import FurnitureInfoTabs from '../components/shop/FurnitureInfoTabs';
import { aggregateByFurniture } from '../configurator/price/aggregateByFurniture';
import ProjectGallery from '../components/shop/ProjectGallery';
import Testimonials from '../components/shop/Testimonials';
import CarpenterBanner from '../components/shop/CarpenterBanner';
import Advantages from '../components/shop/Advantages';
import HowToChoosePanels from '../components/shop/HowToChoosePanels';
import PartnerLogos from '../components/shop/PartnerLogos';

const ShareIcon = iconMap.share;
const FavoriteIcon = iconMap.favorite;
const FavoriteBorderIcon = iconMap.favorite_border;
const SearchOffIcon = iconMap.search_off;

// Tos pačios 6 baldo tipo etiketės, kaip FurnitureFilters.jsx FILTER_CONFIG.types —
// verčiamos per t('furniture_types.type_<n>'), žr. getTypeLabel komponento viduje.

const useStyles = makeStyles({
  page: { minHeight: '100vh', backgroundColor: stonePalette[50] },
  container: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    // Turi sutapti su TopBar.jsx/MainMenu.jsx konteinerių paddingu (žr. "Visuose
    // puslapiuose header ir content turi lygiuoti vertikaliai").
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },

  // ── Kraunama (skeleton) ──
  loadingPad: { paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL },
  loadingCrumb: { marginBottom: tokens.spacingVerticalXXL, width: '96px' },
  loadingGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr' } },
  loadingImage: { aspectRatio: '4 / 3', borderRadius: tokens.borderRadius2XLarge, display: 'block' },
  loadingRightCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },

  // ── Klaida / nerasta ──
  errorWrap: { minHeight: '100vh', backgroundColor: stonePalette[50], display: 'flex', alignItems: 'center', justifyContent: 'center' },
  errorInner: { textAlign: 'center' },
  errorIconChip: {
    width: '64px', height: '64px', borderRadius: tokens.borderRadiusXLarge, backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto', marginRight: 'auto',
    marginBottom: tokens.spacingVerticalM, boxShadow: tokens.shadow4, color: tokens.colorNeutralForeground3,
  },
  errorHeading: { marginBottom: tokens.spacingVerticalS },

  // ── Breadcrumb ──
  breadcrumbBar: {
    backgroundColor: tokens.colorNeutralBackground1, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
  },
  breadcrumbNoMargin: { marginBottom: 0 },

  // ── Pagrindinė sekcija ──
  mainPad: { paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL },
  mainGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr' } },

  // Grid'o eilutė ištempia šią kortelę iki kairėje esančios nuotraukų galerijos
  // aukščio (align-items:stretch), bet be flex-column jos VIDUS liktų natūralaus
  // (mažesnio) aukščio — CTA mygtukai kabodavo per anksti, neatitikdami apačios
  // (žr. /admin/bugs "Sie du mygtukai turi lygiuoti su sio bloko apacia").
  rightCard: { display: 'flex', flexDirection: 'column', height: '100%' },

  // ── Dešinys blokas ──
  rightTopRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  rightLeftCol: { minWidth: 0 },
  badge: { marginBottom: tokens.spacingVerticalS },
  codeHeading: { fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightBold, fontSize: tokens.fontSizeBase500, color: tokens.colorNeutralForeground1, margin: 0 },
  dimsText: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300, marginTop: '2px' },
  rightActions: { display: 'flex', gap: tokens.spacingHorizontalXS, flexShrink: 0 },
  iconBtn: {
    color: tokens.colorNeutralForeground2,
  },
  favActive: {
    backgroundColor: tokens.colorPaletteRedBackground2, borderColor: tokens.colorPaletteRedBorder2, color: tokens.colorPaletteRedForeground2,
    ':hover': { backgroundColor: tokens.colorPaletteRedBackground2, borderColor: tokens.colorPaletteRedBorder2, color: tokens.colorPaletteRedForeground2 },
  },

  priceText: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, marginTop: tokens.spacingVerticalM, marginBottom: 0, fontVariantNumeric: 'tabular-nums' },
  priceNote: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightRegular, color: tokens.colorNeutralForeground3, marginLeft: tokens.spacingHorizontalS },

  servicesBlock: { marginTop: tokens.spacingVerticalM, paddingTop: tokens.spacingVerticalM, borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  servicesEyebrow: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' },
  servicesList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  serviceRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  servicePrice: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },

  fineprint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS },

  // Viena eilutė (ne dvi sukrautos) 480px+ pločio ekranuose — žr. 18 punktų sąrašo #11.
  ctaRow: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalS, marginTop: 'auto', paddingTop: tokens.spacingVerticalM,
    '@media (min-width: 480px)': { flexDirection: 'row' },
  },
  ctaPrimary: { flex: 1, justifyContent: 'center', fontSize: tokens.fontSizeBase300, whiteSpace: 'nowrap' },
  ctaSecondary: { flex: 1, justifyContent: 'center', fontSize: tokens.fontSizeBase300, whiteSpace: 'nowrap' },

  infoTabsWrap: { marginTop: tokens.spacingVerticalL },

  toast: {
    position: 'fixed', bottom: tokens.spacingVerticalL, left: '50%', transform: 'translateX(-50%)',
    backgroundColor: tokens.colorNeutralBackgroundInverted, color: tokens.colorNeutralForegroundInverted,
    paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusXLarge, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium,
    boxShadow: tokens.shadow28, zIndex: tokens.zIndexPopup,
    animationDuration: tokens.durationSlower, animationTimingFunction: tokens.curveEasyEase,
    animationName: { from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' }, to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' } },
  },
});

const FurnitureItem = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const s = useStyles();
  const { t } = useTranslation();
  const getTypeLabel = (type) => (type != null && type >= 0 && type <= 5 ? t(`furniture_types.type_${type}`) : t('furniture_list.generic_type'));
  const [furniture, setFurniture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  // Papildomos paslaugos (surinkimas/pristatymas/užnešimas) — vartotojas PATS
  // pasirenka, kurias nori (nepažymėtos pagal nutylėjimą, kaip senajame
  // fitit.lt puslapyje) prieš pridedant į krepšelį; kainos — TOS PAČIOS
  // formulės, kaip konfiguratoriaus "Paslaugos" žingsnyje (calculatePrice.js),
  // niekur naujai neišgalvotos.
  const [selectedServices, setSelectedServices] = useState({ assembly: false, delivery: false, carry: false });
  const [assemblyPdfOpen, setAssemblyPdfOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/furnitures/${code}`, { signal: controller.signal })
      .then(r => {
        if (r.status === 404) { setError(t('furniture_item.not_found')); return null; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        if (!d) return;
        setFurniture(d);
        document.title = `${d.code} | Fitit`;
        const favs = JSON.parse(localStorage.getItem('fitit_favorites') || '[]');
        setIsFavorite(favs.includes(d.code));
      })
      .catch(err => { if (err.name !== 'AbortError') setError(t('furniture_item.error_loading')); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [code]);

  const toggleFavorite = useCallback(() => {
    if (!furniture) return;
    setIsFavorite(prev => {
      const next = !prev;
      const favs = JSON.parse(localStorage.getItem('fitit_favorites') || '[]');
      const updated = next ? [...favs, furniture.code] : favs.filter(f => f !== furniture.code);
      localStorage.setItem('fitit_favorites', JSON.stringify(updated));
      if (isAuthenticated) {
        fetch(`${API_BASE}/user/favorites/toggle/${encodeURIComponent(furniture.code)}`, {
          method: 'POST', credentials: 'include',
        }).catch(() => {});
      }
      return next;
    });
  }, [furniture, isAuthenticated]);

  const handleShare = useCallback(() => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: furniture?.code, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setToast(t('furniture_item.link_copied'));
        setTimeout(() => setToast(''), 2500);
      }).catch(() => {});
    }
  }, [furniture]);

  // Surinkimo/pristatymo/užnešimo kainos šitam konkrečiam baldui — ta pati
  // calculatePrice.js formulė, kurią jau naudoja konfiguratoriaus "Paslaugos"
  // žingsnis ir Suvestinė, tik čia paleista be redagavimo konteksto vien
  // kainoms paskaičiuoti.
  const servicePrices = useMemo(() => {
    if (!furniture?.cabinets?.length) return { assemblyPrice: 0, carryPrice: 0, deliveryPrice: 45 };
    const settings = { ...(furniture.settings || {}), cabinets: furniture.cabinets, sectionOptions: furniture.settings?.sectionOptions || {} };
    const { perFurniture } = aggregateByFurniture(furniture.cabinets, settings);
    return perFurniture.reduce((sum, f) => ({
      assemblyPrice: sum.assemblyPrice + (f.calc?.assemblyPrice || 0),
      carryPrice: sum.carryPrice + (f.calc?.carryPrice || 0),
      deliveryPrice: Math.max(sum.deliveryPrice, f.calc?.deliveryPrice || 0),
    }), { assemblyPrice: 0, carryPrice: 0, deliveryPrice: 45 });
  }, [furniture]);

  const servicesTotal =
    (selectedServices.assembly ? servicePrices.assemblyPrice : 0) +
    (selectedServices.delivery ? servicePrices.deliveryPrice : 0) +
    (selectedServices.carry ? servicePrices.carryPrice : 0);

  const handleBuyNow = useCallback(() => {
    if (!furniture) return;
    const cart = JSON.parse(sessionStorage.getItem('furnitureCart') || '[]');
    const already = cart.some(i => i?.code === furniture.code);
    if (!already) {
      const itemWithServices = {
        ...furniture,
        services: {
          assemblyPrice: selectedServices.assembly ? servicePrices.assemblyPrice : 0,
          deliveryPrice: selectedServices.delivery ? servicePrices.deliveryPrice : 0,
          carryPrice: selectedServices.carry ? servicePrices.carryPrice : 0,
        },
      };
      sessionStorage.setItem('furnitureCart', JSON.stringify([...cart, itemWithServices]));
      window.dispatchEvent(new Event('cartUpdated'));
    }
    setToast(already ? t('furniture_item.already_in_cart') : t('furniture_item.added_to_cart'));
    setTimeout(() => setToast(''), 3000);
  }, [furniture, selectedServices, servicePrices]);

  if (loading) {
    return (
      <div className={s.page}>
        <div className={mergeClasses(s.container, s.loadingPad)}>
          <SkeletonItem className={s.loadingCrumb} size={16} />
          <div className={s.loadingGrid}>
            <Skeleton>
              <SkeletonItem className={s.loadingImage} />
            </Skeleton>
            <div className={s.loadingRightCol}>
              <Skeleton>
                <SkeletonItem size={32} style={{ width: '75%' }} />
              </Skeleton>
              <Skeleton>
                <SkeletonItem size={16} style={{ width: '50%' }} />
              </Skeleton>
              <Skeleton>
                <SkeletonItem size={16} />
                <SkeletonItem size={16} style={{ width: '66%' }} />
              </Skeleton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !furniture) {
    return (
      <div className={s.errorWrap}>
        <div className={s.errorInner}>
          <div className={s.errorIconChip}>
            <SearchOffIcon style={{ fontSize: 32 }} />
          </div>
          <Heading as="h2" variant="h2" className={s.errorHeading}>{error || t('furniture_item.not_found')}</Heading>
          <Button appearance="outline" onClick={() => navigate('/baldai')}>{t('furniture_item.back_to_catalog')}</Button>
        </div>
      </div>
    );
  }

  const typeLabel = getTypeLabel(furniture.settings?.type);
  const price = furniture.price || furniture.shopPrice || 0;

  return (
    <div className={s.page}>

      {/* Breadcrumb — standartinis Breadcrumb primityvas (theme/primitives.jsx), ne
          rankomis rašytas Button+span, kad atitiktų 20 punktų sąrašo #13. */}
      <div className={s.breadcrumbBar}>
        <div className={s.container}>
          <Breadcrumb items={[{ label: t('furniture_list.breadcrumb_home'), to: '/' }, { label: t('furniture_list.breadcrumb_catalog'), to: '/baldai' }, { label: furniture.code }]} className={s.breadcrumbNoMargin} />
        </div>
      </div>

      <div className={mergeClasses(s.container, s.mainPad)}>
        <div className={s.mainGrid}>

          {/* Left — slider'is: realistiška nuotrauka (jei sugeneruota) + Išorė/Vidus 3D
              vaizdas + techninis brėžinys — viskas viename komponente (žr. jo pačio
              slides logiką), kad tai atrodytų kaip viena, ne trys atskiros dėžutės.
              Surinkimo instrukcijos nuoroda gyvena TIK "Prekės informacija" skirtuke
              (žr. FurnitureInfoTabs.jsx) — anksčiau čia buvusi atskira kortelė
              dubliuodavo tą pačią nuorodą antrą kartą tame pačiame puslapyje. */}
          <FurnitureDrawingTabs furniture={furniture} />

          {/* Right — vienas glaustas blokas vietoj trijų atskirų kortelių: pavadinimas,
              kaina, paslaugos ir CTA dabar viename vizualiniame vienete su vidiniais
              skirtukais (border-t), ne trimis atskirai apvestomis/šešėliuotomis
              dėžutėmis — mažiau "dėžutės viduje dėžutė" pojūčio, daugiau vietos. */}
          <Card padding={tokens.spacingHorizontalXXL} className={s.rightCard}>
            <div className={s.rightTopRow}>
              <div className={s.rightLeftCol}>
                <Badge color="gray" label={typeLabel} className={s.badge} />
                <h1 className={s.codeHeading}>{furniture.code}</h1>
                {furniture.settings?.generalWidth && furniture.settings?.generalHeight && (
                  <p className={s.dimsText}>
                    {(furniture.settings.generalWidth * 100).toFixed(0)} × {(furniture.settings.generalHeight * 100).toFixed(0)} cm
                  </p>
                )}
              </div>
              <div className={s.rightActions}>
                <Button
                  appearance="outline"
                  shape="circular"
                  icon={isFavorite ? <FavoriteIcon style={{ fontSize: 18 }} /> : <FavoriteBorderIcon style={{ fontSize: 18 }} />}
                  onClick={toggleFavorite}
                  className={mergeClasses(s.iconBtn, isFavorite && s.favActive)}
                  aria-label={isFavorite ? t('furniture_list.remove_favorite') : t('furniture_list.add_favorite')}
                />
                <Button
                  appearance="outline"
                  shape="circular"
                  icon={<ShareIcon style={{ fontSize: 18 }} />}
                  onClick={handleShare}
                  className={s.iconBtn}
                  aria-label={t('furniture_item.share')}
                />
              </div>
            </div>

            {price > 0 && (
              <p className={s.priceText}>
                {formatPrice(price + servicesTotal)}
                {servicesTotal > 0 && <span className={s.priceNote}>{t('furniture_item.with_extra_services')}</span>}
              </p>
            )}

            {/* Papildomos paslaugos — vartotojas pats pasirenka, kainos realios
                (calculatePrice.js), ne dekoratyvios; pasirinkimas persikelia į
                krepšelį per handleBuyNow. */}
            <div className={s.servicesBlock}>
              <p className={s.servicesEyebrow}>{t('furniture_item.additional_services')}</p>
              <div className={s.servicesList}>
                {[
                  { key: 'assembly', label: t('furniture_item.service_assembly'), price: servicePrices.assemblyPrice },
                  { key: 'delivery', label: t('furniture_item.service_delivery'), price: servicePrices.deliveryPrice },
                  { key: 'carry', label: t('furniture_item.service_carry'), price: servicePrices.carryPrice },
                ].sort((a, b) => a.price - b.price).map(({ key, label, price: p }) => (
                  <div key={key} className={s.serviceRow}>
                    <Checkbox
                      label={label}
                      checked={selectedServices[key]}
                      onChange={(_e, data) => setSelectedServices(prev => ({ ...prev, [key]: data.checked }))}
                    />
                    <span className={s.servicePrice}>{formatPrice(p, { showCents: false })}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className={s.fineprint}>
              {t('furniture_item.fineprint')}
            </p>

            {/* CTA — "Redaguoti" pirmas (alternatyvus kelias), "Pirkti dabar"
                antras kaip pagrindinis veiksmas (žr. #9: sukeista tvarka,
                be ikonų, tekstas telpa į vieną eilutę). */}
            <div className={s.ctaRow}>
              <Button
                appearance="outline" size="large" className={s.ctaSecondary}
                onClick={() => navigate(`/konfiguratorius/${furniture.code}`)}
              >
                {t('furniture_item.edit')}
              </Button>

              <Button
                appearance="primary" size="large" className={s.ctaPrimary}
                onClick={handleBuyNow}
              >
                {t('furniture_item.buy_now')}
              </Button>
            </div>
          </Card>

        </div>

        {/* Skirtukai (Apžvalga/Prekės informacija/Svoris ir matmenys/Rinkinyje yra) —
            IKEA produkto puslapio informacijos architektūra, žr. FurnitureInfoTabs.jsx. */}
        <div className={s.infoTabsWrap}>
          <FurnitureInfoTabs furniture={furniture} typeLabel={typeLabel} onOpenAssemblyPdf={() => setAssemblyPdfOpen(true)} />
        </div>
      </div>

      {/* Baldo puslapio bendra struktūra (ClickUp 86cbbnvqg) — likę blokai atkartoja
          anksčiau fitit.lt naudotą struktūrą/turinį. */}
      <ProjectGallery typeLabel={typeLabel} />
      <Testimonials />
      <CarpenterBanner />
      <Advantages />
      <HowToChoosePanels />
      <PartnerLogos />

      <AssemblyInstructionsModal code={furniture.code} open={assemblyPdfOpen} onClose={() => setAssemblyPdfOpen(false)} />

      {/* Toast */}
      {toast && (
        <div className={s.toast}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default FurnitureItem;
